import type {
  ChatSession,
  ChatMessage,
  StreamEvent,
} from '@mem-ai-hub/shared';
import { apiClient, getAccessToken } from './api';

export async function createSession(
  modelId: string,
  title?: string,
): Promise<ChatSession> {
  return apiClient.post<ChatSession>('/chat/sessions', { modelId, title });
}

export async function getSessions(
  page = 1,
  pageSize = 20,
  search?: string,
): Promise<{ sessions: ChatSession[]; totalCount: number }> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (search) params.set('search', search);
  return apiClient.get<{ sessions: ChatSession[]; totalCount: number }>(
    `/chat/sessions?${params}`,
  );
}

export async function getSession(
  id: string,
): Promise<{ session: ChatSession; messages: ChatMessage[] }> {
  return apiClient.get<{ session: ChatSession; messages: ChatMessage[] }>(
    `/chat/sessions/${id}`,
  );
}

export async function deleteSession(id: string): Promise<void> {
  await apiClient.delete<void>(`/chat/sessions/${id}`);
}

export async function renameSession(id: string, title: string): Promise<void> {
  await apiClient.patch<void>(`/chat/sessions/${id}`, { title });
}

export interface StreamCallbacks {
  onToken: (content: string) => void;
  onThinking: (content: string) => void;
  onDone: (messageId: string, title?: string) => void;
  onError: (error: string) => void;
}

/**
 * Streams a message response using fetch + ReadableStream SSE parsing.
 * Returns an abort function to cancel the stream.
 */
export function streamMessage(
  sessionId: string,
  content: string,
  deepThinking: boolean,
  callbacks: StreamCallbacks,
  attachmentIds?: string[],
): () => void {
  const controller = new AbortController();

  (async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`/api/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content,
          deepThinking,
          ...(attachmentIds && attachmentIds.length > 0 ? { attachmentIds } : {}),
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorBody = await response.text();
        callbacks.onError(errorBody || `HTTP ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError('Response body is not readable');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          // Keep the last potentially incomplete line in the buffer
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || !trimmed.startsWith('data: ')) continue;

            const jsonStr = trimmed.slice(6);
            if (jsonStr === '[DONE]') continue;

            try {
              const event: StreamEvent = JSON.parse(jsonStr);

              switch (event.type) {
                case 'token':
                  if (event.content) callbacks.onToken(event.content);
                  break;
                case 'thinking':
                  if (event.content) callbacks.onThinking(event.content);
                  break;
                case 'done':
                  callbacks.onDone(event.messageId ?? '', event.title);
                  break;
                case 'error':
                  callbacks.onError(event.error ?? 'Unknown streaming error');
                  break;
              }
            } catch {
              // Skip malformed JSON lines
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (err: unknown) {
      if ((err as Error).name === 'AbortError') return;
      callbacks.onError(
        err instanceof Error ? err.message : 'Stream connection failed',
      );
    }
  })();

  return () => controller.abort();
}
